/** The two interchangeable renderings of one occupation series. */
export type OccupationForm = 'area' | 'ladder';

/** Ordered cycle used by the popup's single cycle button. */
export const OCCUPATION_FORMS: readonly OccupationForm[] = ['area', 'ladder'];

/** One route's claim on one infrastructure, over a half-open interval [startMs, endMs). */
export interface OccupationClaim {
  siteUid: string;
  infrastructureUid: string;
  routeUid: string;
  stopoverUid: string;
  quantity: number;
  startMs: number;
  endMs: number;
}

/** A route holding part of a step's usage. */
export interface OccupationHolder {
  routeUid: string;
  stopoverUid: string;
  quantity: number;
}

/** A constant-usage slice of the timeline, with the claims responsible for it. */
export interface OccupationStep {
  fromMs: number;
  toMs: number;
  used: number;
  holders: OccupationHolder[];
}

/** One lane of the slot ladder: claims that never overlap, sharing a single unit. */
export type OccupationLane = OccupationClaim[];

export interface OccupationSeries {
  siteUid: string;
  infrastructureUid: string;
  /** The infrastructure's name, falling back to its uid when no name was resolved. */
  infrastructureName: string;
  /** Declared availability; null when the site declares none for this infrastructure. */
  capacity: number | null;
  /**
   * False when no series in the set carried a single claim. A plan whose usages were never
   * derived — one solved before the backend produced them, or assembled from prescribed
   * routes rather than optimized — is indistinguishable from a genuinely idle plan series
   * by series, so the whole set decides once and every series carries the same verdict.
   */
  usagesReported: boolean;
  steps: OccupationStep[];
  lanes: OccupationLane[];
  peak: number;
  /** null when capacity is null or zero — never Infinity. */
  peakUtilization: number | null;
  meanUtilization: number | null;
  busyMs: number;
  overMs: number;
}

/** One site's declared availability of one infrastructure. */
export interface OccupationAvailability {
  siteUid: string;
  infrastructureUid: string;
  quantity: number;
}

/**
 * One fetch's worth of the pieces a series is made of. Occupation is contested *between*
 * routes, so the claims come from every route stopping at a site, never from the routes
 * a table happens to have selected.
 */
export interface OccupationContribution {
  claims: OccupationClaim[];
  availabilities: OccupationAvailability[];
  infrastructureNames: ReadonlyMap<string, string>;
}

export type OccupationState = 'over' | 'tight' | 'ok' | 'unknown' | 'unreported';

/**
 * The single source of truth for a series' state. Status is never carried by color
 * alone, so every renderer pairs this with an icon and a translated word.
 */
export function occupationState(series: OccupationSeries): OccupationState {
  // Ordered first: with nothing reported there is no usage to weigh against capacity, and
  // calling 0 of 4 docks headroom would assert an idleness the data cannot support.
  if (!series.usagesReported) return 'unreported';
  if (series.capacity === null || series.capacity <= 0) return 'unknown';
  if (series.overMs > 0) return 'over';
  if (series.peakUtilization !== null && series.peakUtilization >= 1) return 'tight';
  return 'ok';
}
